import { AlertTriangle, ChevronDown } from "lucide-react";
import { useId } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Slider,
} from "@opencloud/ui";
import { agentBrand } from "../brand.js";
import {
  asSentence,
  accountOwnerLabel,
  pickerProviderLabel,
  choiceKey,
  type ComposerRuntimeProblem,
  type ComposerRuntimeView,
} from "../agent-models.js";
import type { AiModelChoice, AiModelPickerOption } from "../types.js";

/** The dashboard's AI providers page and its Connect a provider subpage. */
export interface AiProviderPages {
  manageUrl: string;
  connectUrl: string;
  /** Organisation installations, where only organisation admins add providers. */
  organisation: boolean;
}

export interface ComposerRuntime extends ComposerRuntimeView {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChoiceChange: (choice: AiModelChoice) => void;
  onReasoningEffortChange: (reasoningEffort: string) => void;
  /** App chats name the app in the automatic-tasks note; null for private chats. */
  appName: string | null;
  providerPages: AiProviderPages;
}

/**
 * One model and reasoning picker: models grouped by provider, so a pick is a
 * provider and a model together, and a reasoning slider from Low to Maximum.
 * A choice applies from the next message.
 */
export function ComposerRuntimeControls({ runtime }: { runtime: ComposerRuntime }) {
  const baseId = useId();
  const modelHeading = `${baseId}-model`;
  const levels = runtime.reasoningEfforts;
  const effortIndex = Math.max(0, levels.findIndex((option) => option.id === runtime.reasoningEffort));
  const effortLabel = levels[effortIndex]?.label ?? runtime.reasoningEffort;
  const options = new Map<string, AiModelPickerOption>(runtime.groups.flatMap((group) =>
    group.options.map((option) => [choiceKey(option.choice), option] as const)));
  const selectedKey = runtime.choice ? choiceKey(runtime.choice) : "";
  const pick = runtime.modelLabel && runtime.providerLabel
    ? `${runtime.modelLabel} via ${runtime.providerLabel}` : "Choose a model";
  const selectedGroup = runtime.groups.find(group => group.options.some(option => choiceKey(option.choice) === selectedKey));
  const selectedOwner = selectedGroup && runtime.selectionScope === "participant" ? accountOwnerLabel(selectedGroup) : null;
  return (
    <Popover open={runtime.open} onOpenChange={(open) => runtime.onOpenChange(open)}>
      {/* Only the model shows; the picker names its provider, reasoning and any non-default agent. */}
      <PopoverTrigger render={<Button type="button" variant="ghost" size="sm" />}
        className="a3-composer-runtime" aria-label={`Model and reasoning: ${pick}, ${effortLabel}`}>
        <span>{runtime.modelLabel ?? "Choose a model"}</span>
        {selectedOwner ? <span className="a3-runtime-note"> · {selectedOwner}</span> : null}
        <ChevronDown aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={6}
        className="a3-theme a3-runtime-popover" data-oc-theme={agentBrand().themeId}>
        {runtime.engineLabel ? (
          <div className="a3-runtime-section">
            <div className="a3-runtime-heading-row">
              <span className="a3-runtime-heading">Agent</span>
              <span className="a3-runtime-value">{runtime.engineLabel}</span>
            </div>
            <p className="a3-runtime-note">{runtime.engineFullName ?? runtime.engineLabel}</p>
          </div>
        ) : null}
        <div className="a3-runtime-section">
          <span className="a3-runtime-heading" id={modelHeading}>Model</span>
          {runtime.groups.some((group) => group.options.length) ? (
            <RadioGroup aria-labelledby={modelHeading} className="a3-runtime-groups" value={selectedKey}
              onValueChange={(value) => {
                const option = options.get(String(value));
                if (option) runtime.onChoiceChange(option.choice);
              }}>
              {runtime.groups.map((group, groupIndex) => group.options.length ? (
                <div key={group.id} role="group" aria-labelledby={`${baseId}-group-${groupIndex}`}
                  className="a3-runtime-group" data-provider={group.provider ?? "included"}>
                  <div className="a3-runtime-group-head" id={`${baseId}-group-${groupIndex}`}>
                    <strong>{group.label}</strong>
                    {accountOwnerLabel(group) ? <span className="a3-runtime-note">{accountOwnerLabel(group)}</span> : null}
                  </div>
                  {group.options.map((option, optionIndex) => {
                    const key = choiceKey(option.choice);
                    const selected = key === selectedKey;
                    const optionId = `${baseId}-option-${groupIndex}-${optionIndex}`;
                    const described = option.problem ? `${optionId}-problem` : "";
                    return (
                      <Label key={key} className="a3-runtime-model" data-selected={selected || undefined}
                        data-unavailable={option.problem ? true : undefined}>
                        {/* Named "<model> via <provider>": the same model can come from two providers. */}
                        <RadioGroupItem value={key} disabled={option.problem !== null && !selected}
                          aria-labelledby={`${optionId}-name ${optionId}-via`}
                          {...(described ? { "aria-describedby": described } : {})} />
                        <span className="a3-runtime-model-name">
                          <span>
                            <span id={`${optionId}-name`}>{option.label}</span>
                            <span id={`${optionId}-via`} className="sr-only"> via {pickerProviderLabel(group)}</span>
                          </span>
                          {option.problem ? (
                            <span className="a3-runtime-model-problem" id={`${optionId}-problem`}>
                              {option.problem.message}
                            </span>
                          ) : null}
                        </span>
                      </Label>
                    );
                  })}
                </div>
              ) : null)}
            </RadioGroup>
          ) : (
            <p className="a3-runtime-empty">No models are available yet.</p>
          )}
        </div>
        <div className="a3-runtime-section">
          <div className="a3-runtime-heading-row">
            <span className="a3-runtime-heading">Reasoning</span>
            <span className="a3-runtime-value">{effortLabel}</span>
          </div>
          <Slider min={0} max={Math.max(0, levels.length - 1)} step={1} value={[effortIndex]}
            getAriaLabel={() => "Reasoning"}
            getAriaValueText={(_formatted, value) => levels[value]?.label ?? String(value)}
            onValueChange={(value) => {
              const option = levels[Array.isArray(value) ? value[0] ?? 0 : value];
              if (option) runtime.onReasoningEffortChange(option.id);
            }} />
          <div className="a3-runtime-scale" aria-hidden="true">
            <span>{levels[0]?.label}</span>
            <span>{levels.at(-1)?.label}</span>
          </div>
        </div>
        <div className="a3-runtime-note-row">
          <p className="a3-runtime-note">
            Applies from your next message.
            {runtime.appName ? runtime.selectionScope === "participant"
              ? " Your choice applies to your messages only. Automatic tasks keep the app's selected model."
              : runtime.automatic?.source === "override"
              ? ` Automatic tasks in ${runtime.appName} keep the model set on its AI page.`
              : ` Automatic tasks in ${runtime.appName} follow your latest pick.` : null}
          </p>
          <ProviderLink runtime={runtime} />
        </div>
        {runtime.ownerSharingAllowed === false ? <p className="a3-runtime-manage-note">
          The app owner has turned off sharing their provider. Choose your own provider, or ask them to allow it on the app&apos;s AI page.
        </p> : null}
        <ProviderNote runtime={runtime} />
      </PopoverContent>
    </Popover>
  );
}

/** Admins reach the providers page from a text link beside the note. */
function ProviderLink({ runtime }: { runtime: ComposerRuntime }) {
  if (!runtime.canManageProviders) return null;
  const pages = runtime.providerPages;
  const connect = !runtime.groups.some((group) => group.id !== "included");
  return (
    <a className="a3-runtime-manage" href={connect ? pages.connectUrl : pages.manageUrl}>
      {connect ? "Connect a provider" : "Manage models"}
    </a>
  );
}

/** People who can't manage providers on an organisation learn who can. */
function ProviderNote({ runtime }: { runtime: ComposerRuntime }) {
  return !runtime.canManageProviders && runtime.providerPages.organisation
    ? <p className="a3-runtime-manage-note">Ask an organisation admin to add models.</p>
    : null;
}

/** Above the composer when the pick the next message would use can't run. */
export function ComposerRuntimeProblemAlert({ problem }: { problem: ComposerRuntimeProblem }) {
  return (
    <Alert className="a3-runtime-problem">
      <AlertTriangle aria-hidden="true" />
      <AlertTitle>{problem.modelLabel} via {problem.providerLabel} can't run</AlertTitle>
      <AlertDescription>
        {asSentence(problem.message)} Pick another model to send your next message.
      </AlertDescription>
    </Alert>
  );
}
